import { motion } from 'framer-motion'
import { Shield, Cookie, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const Privacy_policy = () => {
  const { t } = useTranslation()
  const lastUpdated = '06.07.2026'

  return (
    <div className="pt-16 overflow-x-hidden">
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <Shield className="w-4 h-4 mr-2" />
              {t('privacyPage.title')}
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 break-words">
              {t('privacyPage.title')} <span className="gradient-text">{t('privacyPage.headline')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('privacyPage.description')}
            </p>
            <p className="text-sm text-gray-500 mt-4">{t('privacyPage.lastUpdated')}: {lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 space-y-10">

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacyPage.section1.description')}
                  </p>
                  
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
                    <p className="text-green-800">
                      {t('privacyPage.keyPoints.noCookies.description')}
                    </p>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacyPage.keyPoints.minimalData.description')}
                  </p>
                  
                  <div className="bg-primary-50 rounded-lg p-6 my-6">
                    <h3 className="font-semibold text-primary-900 mb-3">{t('privacyPage.keyPoints.dataProtection')}</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-primary-800">{t('privacyPage.section2.purpose1')}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-primary-800">{t('privacyPage.section2.purpose2')}</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-primary-800">{t('privacyPage.section2.purpose2.description')}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6">
                    <p className="text-red-800 font-medium">
                      {t('privacyPage.keyPoints.dataProtection.description')}
                    </p>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    {t('privacyPage.contact.description')} <a href="mailto:infotechprosubkg@gmail.com" className="text-primary-600 hover:text-primary-700 underline">infotechprosubkg@gmail.com</a>.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className="bg-gradient-to-r from-accent-50 to-purple-50 rounded-2xl p-8 my-8">
                  <div className="flex items-center mb-4">
                    <Cookie className="w-8 h-8 text-accent-600 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.keyPoints.title')}</h2>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {t('privacyPage.keyPoints.description')}
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.keyPoints.noCookies.description1')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.keyPoints.minimalData.description1')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.section2.purpose')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.keyPoints.dataProtection.description1')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.section1.noSensitive')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.section6.description1')}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
                      </div>
                      <p className="text-gray-700">{t('privacyPage.section8.additionalRights1')}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mt-6">
                    {t('privacyPage.contact.description1')}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">1</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section1.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{t('privacyPage.section1.subtitle')}</h3>
                      <p className="text-gray-700 leading-relaxed">
                        {t('privacyPage.section1.description1')}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {t('privacyPage.section1.collectList')}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.name')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.email')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.phone')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.company')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.service')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.inquiry')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-700 leading-relaxed mb-3 font-medium">
                        {t('privacyPage.section1.vacancySubtitle')}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.vacancyTitle')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.coverLetter')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.resume')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{t('privacyPage.section1.technicalSubtitle')}</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {t('privacyPage.section1.technicalDescription')}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.technicalLanguage')}</span>
                          </li>
                          <li className="flex items-center">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                            <span className="text-gray-700">{t('privacyPage.section1.technicalIp')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <p className="text-yellow-800">
                        {t('privacyPage.section1.noSensitive')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section1.accuracy')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">2</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section2.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section2.description')}
                    </p>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="font-semibold text-blue-900 mb-2">{t('privacyPage.section2.purpose1.description1')}</p>
                        <p className="text-blue-800">
                          {t('privacyPage.section2.purpose1.description')}
                        </p>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="font-semibold text-blue-900 mb-2">{t('privacyPage.section2.purpose2.description1')}</p>
                        <p className="text-blue-800">
                          {t('privacyPage.section2.purpose2.description2')}
                        </p>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="font-semibold text-blue-900 mb-2">{t('privacyPage.section2.purpose3.description1')}</p>
                        <p className="text-blue-800">
                          {t('privacyPage.section2.purpose3.description')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('privacyPage.section2.noAutomated')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section2.onlyNecessary')}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">3</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section3.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section3.description')}
                    </p>

                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800">{t('privacyPage.section3.consent')}</p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section3.legitimateInterest')}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section3.noOtherBases')}
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 italic">
                        {t('privacyPage.section3.explanation')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">4</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section4.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-green-800 font-medium">
                        {t('privacyPage.section4.noSell')}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {t('privacyPage.section4.limitedCases')}
                      </p>
                      <div className="space-y-3">
                        <div className="bg-orange-50 rounded-lg p-4">
                          <p className="font-semibold text-orange-900 mb-2">{t('privacyPage.section4.serviceProviders')}</p>
                          <p className="text-orange-800">
                            {t('privacyPage.section4.serviceProviders.description')}
                          </p>
                        </div>
                        
                        <div className="bg-orange-50 rounded-lg p-4">
                          <p className="font-semibold text-orange-900 mb-2">{t('privacyPage.section4.legalRequirements')}</p>
                          <p className="text-orange-800">
                            {t('privacyPage.section4.legalRequirements.description')}
                          </p>
                        </div>

                        <div className="bg-orange-50 rounded-lg p-4">
                          <p className="font-semibold text-orange-900 mb-2">{t('privacyPage.section4.externalServices')}</p>
                          <p className="text-orange-800">
                            {t('privacyPage.section4.externalServices.description')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <p className="text-red-800">
                        {t('privacyPage.section4.noAffiliates')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">5</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section5.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section5.description')}
                    </p>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('privacyPage.section5.practice')}
                      </p>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('privacyPage.section5.recruitment')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('privacyPage.section5.noFixedPeriods')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">6</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section6.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section6.description')}
                    </p>
                    
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <p className="text-yellow-800">
                        {t('privacyPage.section6.disclaimer')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section6.recommendation')}
                    </p>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('privacyPage.section6.fileUpload')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">7</span>
                    </div>
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 break-words">{t('privacyPage.section7.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <p className="text-yellow-800">
                        {t('privacyPage.section7.no knowingly')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section7.intendedUse')}
                    </p>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('privacyPage.section7.contactMinors')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">8</span>
                    </div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 break-words">{t('privacyPage.section8.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section8.description')}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section8.additionalRights')}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section8.exerciseRights')}
                    </p>
                    
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-green-800">
                        {t('privacyPage.section8.response')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">9</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section9.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section9.noTracking')}
                    </p>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('privacyPage.section9.localStorage')}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('privacyPage.section9.noDNT')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">10</span>
                    </div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 break-words">{t('privacyPage.section10.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section10.description')}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section10.requestRights')}
                    </p>
                    
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <p className="text-green-800">
                        {t('privacyPage.section10.noTargetedAdvertising')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section10.exercisePrivacyRights')}
                    </p>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('privacyPage.section10.response')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.6 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 p-6 my-8">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-primary-600 font-bold">11</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('privacyPage.section11.title')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800">
                        {t('privacyPage.section11.yes')}
                      </p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {t('privacyPage.section11.updatedVersion')}
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">
                        {t('privacyPage.section11.reviewPeriodically')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 text-white"
              >
                <div className="flex items-center mb-6">
                  <Mail className="w-12 h-12 mr-4"/>
                  <div>
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-white font-bold">12</span>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold break-words">{t('privacyPage.section12.title')}</h2>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <p className="text-primary-100 leading-relaxed">
                    {t('privacyPage.section12.description')}
                  </p>
                  
                  <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                    <p className="font-semibold text-white mb-3">{t('privacyPage.contact.company')}</p>
                    <div className="space-y-1 text-primary-100">
                      <p>{t('privacyPage.contact.address')}</p>
                      <p>{t('privacyPage.contact.district')}</p>
                      <p>{t('privacyPage.contact.city')}</p>
                      <p>{t('privacyPage.contact.country')}</p>
                    </div>
                  </div>
                </div>
              </motion.div>


            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Privacy_policy